// src/utils/logger.js
/**
 * Sistema de logging configurable por módulo
 * 
 * Uso:
 * import { createLogger } from '../utils/logger';
 * const logger = createLogger('FAR');
 * 
 * logger.debug('Mensaje de debug');
 * logger.info('Información general');
 * logger.warn('Advertencia');
 * logger.error('Error crítico');
 */

// Niveles de logging
export const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  OFF: 4
};

// Configuración por defecto de módulos
const DEFAULT_MODULE_CONFIG = {
  FAR: LOG_LEVELS.INFO,     // Fondos a Rendir
  RCV: LOG_LEVELS.INFO,     // Registro de Compras y Ventas
  DTE: LOG_LEVELS.INFO,     // Documentos Tributarios Electrónicos
  API: LOG_LEVELS.WARN,     // Llamadas a API
  AUTH: LOG_LEVELS.INFO,    // Autenticación
  UI: LOG_LEVELS.WARN,      // Interfaz de usuario
  REDUX: LOG_LEVELS.WARN,   // Estado de Redux
  GLOBAL: LOG_LEVELS.INFO   // Logger global
};

// Colores para cada nivel (solo en desarrollo)
const LOG_COLORS = {
  [LOG_LEVELS.DEBUG]: '#6B7280', // Gris
  [LOG_LEVELS.INFO]: '#3B82F6',  // Azul
  [LOG_LEVELS.WARN]: '#F59E0B',  // Amarillo
  [LOG_LEVELS.ERROR]: '#EF4444'  // Rojo
};

// Iconos para cada nivel
const LOG_ICONS = {
  [LOG_LEVELS.DEBUG]: '🔍',
  [LOG_LEVELS.INFO]: 'ℹ️',
  [LOG_LEVELS.WARN]: '⚠️',
  [LOG_LEVELS.ERROR]: '❌'
};

/**
 * Clase Logger para un módulo específico
 */
class ModuleLogger {
  constructor(moduleName) {
    this.moduleName = moduleName;
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  /**
   * Obtener el nivel de log configurado para este módulo
   */
  getLogLevel() {
    try {
      // Intentar obtener desde localStorage
      const stored = localStorage.getItem('logger_config');
      if (stored) {
        const config = JSON.parse(stored);
        return config[this.moduleName] ?? DEFAULT_MODULE_CONFIG[this.moduleName] ?? LOG_LEVELS.INFO;
      }
    } catch (error) {
      // Si hay error, usar configuración por defecto
    }
    
    return DEFAULT_MODULE_CONFIG[this.moduleName] ?? LOG_LEVELS.INFO;
  }

  /**
   * Verificar si un nivel debe ser loggeado
   */
  shouldLog(level) {
    const moduleLevel = this.getLogLevel();
    return level >= moduleLevel && moduleLevel !== LOG_LEVELS.OFF;
  }

  /**
   * Formatear mensaje con timestamp y módulo
   */
  formatMessage(level, message, ...args) {
    const timestamp = new Date().toLocaleTimeString();
    const levelName = Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === level);
    const icon = LOG_ICONS[level] || '';
    
    return {
      prefix: `${icon} [${timestamp}] [${this.moduleName}] [${levelName}]`,
      message,
      args
    };
  }

  /**
   * Realizar el logging real
   */
  doLog(level, message, ...args) {
    if (!this.shouldLog(level)) return;

    const { prefix, message: msg, args: logArgs } = this.formatMessage(level, message, ...args);
    const color = LOG_COLORS[level];

    // En desarrollo, usar colores y estilos
    if (this.isDevelopment && color) {
      // Log con estilos en desarrollo
      console.log(
        `%c${prefix}%c ${msg}`,
        `color: ${color}; font-weight: bold;`,
        'color: inherit;',
        ...logArgs
      );
    } else {
      // En producción, logging simple
      // Log simple en producción
      console.log(prefix, msg, ...logArgs);
    }
  }

  /**
   * Métodos públicos de logging
   */
  debug(message, ...args) {
    this.doLog(LOG_LEVELS.DEBUG, message, ...args);
  }

  info(message, ...args) {
    this.doLog(LOG_LEVELS.INFO, message, ...args);
  }

  warn(message, ...args) {
    this.doLog(LOG_LEVELS.WARN, message, ...args);
  }

  error(message, ...args) {
    this.doLog(LOG_LEVELS.ERROR, message, ...args);
  }

  /**
   * Log condicional para debugging
   */
  debugIf(condition, message, ...args) {
    if (condition) {
      this.debug(message, ...args);
    }
  }

  /**
   * Log de grupo (para agrupar logs relacionados)
   */
  group(title, callback) {
    if (this.shouldLog(LOG_LEVELS.DEBUG) && this.isDevelopment) {
      console.group(`🔍 [${this.moduleName}] ${title}`);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    } else {
      callback();
    }
  }

  /**
   * Log de tabla (para mostrar arrays/objetos como tabla)
   */
  table(data, title = 'Data') {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      this.debug(`📊 ${title}:`);
      console.table(data);
    }
  }

  /**
   * Log de tiempo (medir duración de operaciones)
   */
  time(label) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      console.time(`⏱️ [${this.moduleName}] ${label}`);
    }
  }

  timeEnd(label) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      console.timeEnd(`⏱️ [${this.moduleName}] ${label}`);
    }
  }
}

/**
 * Cache de loggers por módulo
 */
const loggerCache = new Map();

/**
 * Crear o obtener logger para un módulo
 */
export function createLogger(moduleName) {
  if (!loggerCache.has(moduleName)) {
    loggerCache.set(moduleName, new ModuleLogger(moduleName));
  }
  return loggerCache.get(moduleName);
}

/**
 * Configurar niveles de log para módulos
 */
export function configureLogger(moduleConfig) {
  try {
    const currentConfig = getLoggerConfig();
    const newConfig = { ...currentConfig, ...moduleConfig };
    localStorage.setItem('logger_config', JSON.stringify(newConfig));
    
    // Log del cambio
    const globalLogger = createLogger('GLOBAL');
    globalLogger.info('Logger configuration updated:', newConfig);
  } catch (error) {
    console.error('Error saving logger configuration:', error);
  }
}

/**
 * Obtener configuración actual
 */
export function getLoggerConfig() {
  try {
    const stored = localStorage.getItem('logger_config');
    return stored ? JSON.parse(stored) : DEFAULT_MODULE_CONFIG;
  } catch (error) {
    return DEFAULT_MODULE_CONFIG;
  }
}

/**
 * Resetear configuración a valores por defecto
 */
export function resetLoggerConfig() {
  try {
    localStorage.removeItem('logger_config');
    const globalLogger = createLogger('GLOBAL');
    globalLogger.info('Logger configuration reset to defaults');
  } catch (error) {
    console.error('Error resetting logger configuration:', error);
  }
}

/**
 * Habilitar debug para un módulo específico
 */
export function enableDebug(moduleName) {
  configureLogger({ [moduleName]: LOG_LEVELS.DEBUG });
}

/**
 * Deshabilitar completamente un módulo
 */
export function disableModule(moduleName) {
  configureLogger({ [moduleName]: LOG_LEVELS.OFF });
}

/**
 * Funciones de conveniencia para configuración rápida
 */
export const loggerConfig = {
  // Habilitar debug para desarrollo
  development() {
    configureLogger({
      FAR: LOG_LEVELS.DEBUG,
      RCV: LOG_LEVELS.DEBUG,
      API: LOG_LEVELS.INFO,
      REDUX: LOG_LEVELS.INFO
    });
  },

  // Configuración para producción
  production() {
    configureLogger({
      FAR: LOG_LEVELS.WARN,
      RCV: LOG_LEVELS.WARN,
      API: LOG_LEVELS.ERROR,
      REDUX: LOG_LEVELS.OFF,
      UI: LOG_LEVELS.OFF
    });
  },

  // Solo errores
  quiet() {
    Object.keys(DEFAULT_MODULE_CONFIG).forEach(module => {
      configureLogger({ [module]: LOG_LEVELS.ERROR });
    });
  },

  // Silencio total
  silent() {
    Object.keys(DEFAULT_MODULE_CONFIG).forEach(module => {
      configureLogger({ [module]: LOG_LEVELS.OFF });
    });
  }
};

/**
 * Logger global para uso general
 */
export const logger = createLogger('GLOBAL');

/**
 * Utilidad para debugging en consola del navegador
 * Disponible como window.loggerUtils en desarrollo
 */
if (process.env.NODE_ENV === 'development') {
  window.loggerUtils = {
    configure: configureLogger,
    getConfig: getLoggerConfig,
    reset: resetLoggerConfig,
    enable: enableDebug,
    disable: disableModule,
    presets: loggerConfig,
    levels: LOG_LEVELS,
    
    // Funciones de ayuda
    help() {
      // Log de ayuda en desarrollo
      console.log(`
🔍 Logger Utils - Comandos disponibles:

📋 Configuración:
  loggerUtils.configure({ FAR: 0, RCV: 1 })  - Configurar niveles
  loggerUtils.getConfig()                     - Ver configuración actual
  loggerUtils.reset()                         - Resetear a valores por defecto

🎯 Configuración rápida:
  loggerUtils.enable('FAR')                   - Habilitar debug para módulo
  loggerUtils.disable('RCV')                  - Deshabilitar módulo
  
🎨 Presets:
  loggerUtils.presets.development()           - Modo desarrollo
  loggerUtils.presets.production()            - Modo producción
  loggerUtils.presets.quiet()                 - Solo errores
  loggerUtils.presets.silent()                - Silencio total

📊 Niveles disponibles:
  DEBUG: ${LOG_LEVELS.DEBUG}, INFO: ${LOG_LEVELS.INFO}, WARN: ${LOG_LEVELS.WARN}, ERROR: ${LOG_LEVELS.ERROR}, OFF: ${LOG_LEVELS.OFF}

💡 Módulos disponibles: ${Object.keys(DEFAULT_MODULE_CONFIG).join(', ')}
      `);
    }
  };
}